/**
 * SidebarNavControl
 * Renders a vertical sidebar navigation menu.
 * Config: items[] = { id: string; label: string; icon?: string }
 * Value: active item id (string).
 * In view mode, only the active item's label is shown.
 */

import React, { memo, useCallback } from 'react';
import type { FormControl } from '@atlas-forms/types-js';

export interface SidebarNavItem {
  id: string;
  label: string;
  icon?: string;
}

export interface SidebarNavControlProps {
  control: FormControl;
  value?: string;
  onChange?: (v: string) => void;
  mode?: 'edit' | 'view';
}

export const SidebarNavControl = memo(function SidebarNavControl({ control, value, onChange, mode = 'edit' }: SidebarNavControlProps) {
  const cfg = (control as any).config ?? {};
  const items: SidebarNavItem[] = cfg.items ?? [];
  const activeId = value ?? items[0]?.id ?? '';

  const navStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '8px 0',
    minWidth: 180,
  };

  const itemStyle = useCallback((id: string): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 6,
    background: id === activeId ? 'rgba(34,197,94,0.15)' : 'transparent',
    color: id === activeId ? '#22c55e' : '#94a3b8',
    fontWeight: id === activeId ? 600 : 400,
    fontSize: 14,
    cursor: mode !== 'view' ? 'pointer' : 'default',
    border: id === activeId ? '1px solid rgba(34,197,94,0.25)' : '1px solid transparent',
    transition: 'all 0.15s',
    userSelect: 'none',
  }), [activeId, mode]);

  if (mode === 'view') {
    const active = items.find((i) => i.id === activeId);
    return (
      <div data-control-id={control.id} data-control-type="sidebar-nav" style={{ fontSize: 14, color: '#f8fafc' }}>
        {active?.label ?? activeId}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-control-id={control.id} data-control-type="sidebar-nav">
        <span style={{ fontSize: 13, opacity: 0.5, color: '#94a3b8' }}>No nav items configured</span>
      </div>
    );
  }

  return (
    <nav data-control-id={control.id} data-control-type="sidebar-nav" aria-label={control.label ?? 'Sidebar navigation'}>
      <ul style={{ ...navStyle, listStyle: 'none', margin: 0, padding: '8px 0' }}>
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              style={itemStyle(item.id)}
              aria-current={item.id === activeId ? 'page' : undefined}
              onClick={() => onChange?.(item.id)}
            >
              {item.icon && <span style={{ fontSize: 16 }}>{item.icon}</span>}
              <span>{item.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
});
